import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { MainTemplate } from 'templates';
import { LoadingIndicator } from 'atoms';
import { usePandaDocAnalytics, usePandadocSync } from 'core/hooks';

import * as S from './PandaDocAnalytics.styled';
import { DatePresetId, DEFAULT_DATE_PRESET, Granularity } from './constants';
import { Filters } from './components';
import { SyncBanner } from './SyncBanner';
import { SyncChip } from './SyncChip';
import { OverviewTab, TypeTab, CoursesTab, DocumentsTab } from './tabs';
import { fmtDate, fmtNum, presetRange } from './utils';

type TabId =
  | 'overview'
  | 'students'
  | 'dofin'
  | 'trainers'
  | 'courses'
  | 'documents';

const TAB_IDS: TabId[] = [
  'overview',
  'students',
  'dofin',
  'trainers',
  'courses',
  'documents',
];

export const PandaDocAnalytics = memo(() => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('overview');
  const [granularity, setGranularity] = useState<Granularity>('day');
  const [course, setCourse] = useState<string>('all');

  const [datePreset, setDatePreset] = useState<DatePresetId | 'custom'>(
    DEFAULT_DATE_PRESET,
  );
  const [dateRange, setDateRange] = useState(() =>
    presetRange(DEFAULT_DATE_PRESET),
  );
  const { from: dateFrom, to: dateTo } = dateRange;

  const onDatePresetChange = useCallback((id: DatePresetId) => {
    setDatePreset(id);
    setDateRange(presetRange(id));
  }, []);

  const onDateCustomChange = useCallback(
    (from: string | null, to: string | null) => {
      setDatePreset('custom');
      setDateRange({ from, to });
    },
    [],
  );

  // Shell query — all document types; backs Обзор, Сравнение курсов and the
  // header. Type tabs run their own query with a `docType` argument.
  const {
    data,
    loading,
    fetching,
    permissionDenied,
    refetch,
  } = usePandaDocAnalytics({
    granularity,
    course,
    dateFrom,
    dateTo,
  });

  // Manual full re-sync — queues `triggerPandadocSync` and polls until the
  // backend drains the details backlog, then re-queries the analytics shell.
  const { triggerSync, syncing, detailsPending } = usePandadocSync(refetch);

  const subtitle = useMemo(() => {
    const w = data?.window;
    if (!w) {
      return t('pages.pandadocAnalytics.loadingFresh');
    }
    return t('pages.pandadocAnalytics.subtitle', {
      docs: fmtNum(w.docsLoaded),
      total: fmtNum(w.totalInDb),
      signed: fmtNum(w.signedCount),
      from: fmtDate(w.minDate),
      to: fmtDate(w.maxDate),
    });
  }, [data, t]);

  if (permissionDenied) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.PageTitle>{t('pages.pandadocAnalytics.title')}</S.PageTitle>
          <S.EmptyState>{t('pages.pandadocAnalytics.noAccess')}</S.EmptyState>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  if (loading && !data) {
    return (
      <MainTemplate>
        <S.Wrapper>
          <S.CenterLoader>
            <LoadingIndicator />
          </S.CenterLoader>
        </S.Wrapper>
      </MainTemplate>
    );
  }

  const isEmpty = !data || (data.window?.docsLoaded ?? 0) === 0;
  const isAnalyticsTab = activeTab !== 'documents';
  const isTypeTab =
    activeTab === 'students' ||
    activeTab === 'dofin' ||
    activeTab === 'trainers';

  return (
    <MainTemplate>
      <S.Wrapper>
        <SyncChip />
        <S.HeaderRow>
          <S.PageTitle>{t('pages.pandadocAnalytics.title')}</S.PageTitle>
          {/* Always visible — works even when everything is already synced. */}
          <S.SyncAllButton
            type="button"
            disabled={syncing}
            onClick={triggerSync}
          >
            {syncing && <S.Spinner />}
            {!syncing && t('pages.pandadocAnalytics.sync.button')}
            {syncing && detailsPending !== null
              ? t('pages.pandadocAnalytics.sync.chipInProgress', {
                  count: detailsPending,
                })
              : ''}
            {syncing && detailsPending === null
              ? t('pages.pandadocAnalytics.sync.checking')
              : ''}
          </S.SyncAllButton>
          <S.ReloadButton
            type="button"
            disabled={fetching}
            onClick={() => window.location.reload()}
          >
            {t('pages.pandadocAnalytics.reload')}
          </S.ReloadButton>
        </S.HeaderRow>
        <S.SubTitle>{subtitle}</S.SubTitle>

        <SyncBanner />

        <S.TabBar>
          {TAB_IDS.map((tabId) => (
            <S.TabBarItem
              key={tabId}
              type="button"
              active={activeTab === tabId}
              onClick={() => setActiveTab(tabId)}
            >
              {t(`pages.pandadocAnalytics.tabs.${tabId}`)}
            </S.TabBarItem>
          ))}
        </S.TabBar>

        {/* Granularity is irrelevant to Обзор / Сравнение курсов, but the bar
            stays put so filters don't jump around between tabs. */}
        <Filters
          granularity={granularity}
          course={course}
          datePreset={datePreset}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onGranularityChange={setGranularity}
          onCourseChange={setCourse}
          onDatePresetChange={onDatePresetChange}
          onDateCustomChange={onDateCustomChange}
        />

        {activeTab === 'documents' && (
          <DocumentsTab course={course} dateFrom={dateFrom} dateTo={dateTo} />
        )}

        {isTypeTab && (
          <TypeTab
            docType={activeTab as 'students' | 'dofin' | 'trainers'}
            granularity={granularity}
            course={course}
            dateFrom={dateFrom}
            dateTo={dateTo}
          />
        )}

        {isAnalyticsTab && !isTypeTab && isEmpty && (
          <S.EmptyState>{t('pages.pandadocAnalytics.empty')}</S.EmptyState>
        )}

        {isAnalyticsTab && !isTypeTab && !isEmpty && data && (
          <S.DimWhenFetching fetching={fetching}>
            {activeTab === 'overview' && <OverviewTab data={data} />}
            {activeTab === 'courses' && (
              <CoursesTab data={data} granularity={granularity} />
            )}
          </S.DimWhenFetching>
        )}
      </S.Wrapper>
    </MainTemplate>
  );
});
